/**
 * Parent-facing /feedback view.
 *
 * Translated from the Tailwind + lucide-react reference at
 * `design/pages/mobile/FeedbackView.jsx` into MUI 7 +
 * @mui/icons-material. The reference is the spec — when this file
 * and the reference disagree about layout, copy, or visual
 * treatment, the reference wins.
 *
 * Replaces the prior admin-style table at this same route
 * (`/profile/:pid/feedback`). Vocabulary specifically excluded
 * from the parent-facing UI:
 *   - raw video / agent-score IDs
 *   - "Specialization" / "User Specialization"
 *   - "Agreement" as a column header (replaced by the
 *     natural-language AI-agreement line)
 *   - "Original / Agreed", "User Feedback" type chip
 *   - the trash-can affordance — replaced by an actions menu
 *
 * Renders inside the existing `ProfileLayoutResponsive`, so the
 * top-bar / back-breadcrumb already exists upstream. The
 * page-local back link is kept for symmetry with the reference,
 * and the body is everything below it.
 */
import React, {useMemo, useState} from 'react';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {Link as RouterLink, useParams} from 'react-router-dom';
import {formatDistanceToNow, format, isAfter, subDays, subMonths} from 'date-fns';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import InputBase from '@mui/material/InputBase';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import CheckRounded from '@mui/icons-material/CheckRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import RotateRightRounded from '@mui/icons-material/RotateRightRounded';
import ThumbUpRounded from '@mui/icons-material/ThumbUpRounded';
import ThumbDownRounded from '@mui/icons-material/ThumbDownRounded';
import RestartAltRounded from '@mui/icons-material/RestartAltRounded';
import MoreHorizRounded from '@mui/icons-material/MoreHorizRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import PlayCircleRounded from '@mui/icons-material/PlayCircleRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRounded from '@mui/icons-material/ExpandLessRounded';
import {ActivityRow, useRecentActivity} from '../../api/graphql_recent_activity';
import {useGetMyYouTubeProfiles} from '../../api/graphql_profiles';
import {useRetractVerdict, useScoreVideo} from '../../api/graphql_verdict_actions';
import {AgreementType} from '../../types/generated/graphql';

const C = {
  slate50: '#F8FAFC',
  slate100: '#F1F5F9',
  slate200: '#E2E8F0',
  slate300: '#CBD5E1',
  slate400: '#94A3B8',
  slate500: '#64748B',
  slate600: '#475569',
  slate700: '#334155',
  slate800: '#1E293B',
  slate900: '#0F172A',
  indigo50: '#EEF2FF',
  indigo100: '#E0E7FF',
  indigo300: '#A5B4FC',
  indigo400: '#818CF8',
  indigo500: '#6366F1',
  indigo600: '#3448F0',
  indigo700: '#2839C9',
  indigo800: '#1E2A9B',
  emerald100: '#D1FAE5',
  emerald500: '#10B981',
  emerald700: '#047857',
  rose100: '#FFE4E6',
  rose500: '#F43F5E',
  rose600: '#E11D48',
  rose700: '#BE123C',
  amber500: '#F59E0B',
  amber700: '#B45309',
};

// ---------------------------------------------------------------------------
// Domain mapping
// ---------------------------------------------------------------------------

type VerdictKind = 'approved' | 'blocked' | 'skipped';
type TimeScope = 'week' | 'month' | 'all';
type FilterKind = 'all' | VerdictKind;

interface VerdictRow {
  id: string;
  videoId: string;
  verdict: VerdictKind;
  decidedAt: string; // ISO from FeedbackLog.date
  // AI agreement narrative inputs
  aiScore: number | null;          // user's score on AGREE (= AI's), user's override on DISAGREE
  agreement: AgreementType | null; // null when no linked agent score
  // Plain-English explanation, surfaced in the expanded body
  explanation: string;
  // Video metadata — populated async via DataLoader; nulls until then
  title: string | null;
  channelName: string | null;
  thumbUrl: string | null;
  videoUrl: string;
}

function activityToRow(a: ActivityRow): VerdictRow | null {
  // Vote-tone rows only — `ai` rows belong on the Actions taken
  // page, not Your votes.
  const verdict: VerdictKind | null =
    a.tone === 'approved' ? 'approved' : a.tone === 'blocked' ? 'blocked' : null;
  if (!verdict || !a.videoId) return null;
  return {
    id: a.id,
    videoId: a.videoId,
    verdict,
    decidedAt: new Date(a.timestamp).toISOString(),
    // The `recentActivity` resolver doesn't expose AI score / agent
    // agreement today; AINote falls back to its "no AI signal"
    // copy. Wiring those through would be a backend additive change.
    aiScore: null,
    agreement: null,
    explanation: '',
    title: a.videoTitle,
    channelName: a.channelName,
    thumbUrl: a.thumbnailUrl,
    videoUrl: `https://www.youtube.com/watch?v=${a.videoId}`,
  };
}

function relativeOrShortDate(iso: string): string {
  // Reference uses "2h ago", "Yesterday", "3 days ago" for recent
  // entries and falls back to a short date for anything older.
  // date-fns' formatDistanceToNow gives us the first; we cap at
  // ~7 days and switch to a "MMM d" format beyond that.
  const t = new Date(iso);
  if (isNaN(t.getTime())) return '';
  const sevenDaysAgo = subDays(new Date(), 7);
  if (isAfter(t, sevenDaysAgo)) {
    return formatDistanceToNow(t, {addSuffix: true});
  }
  return format(t, 'MMM d');
}

function inScope(iso: string, scope: TimeScope): boolean {
  if (scope === 'all') return true;
  const t = new Date(iso);
  if (isNaN(t.getTime())) return false;
  const cutoff = scope === 'week' ? subDays(new Date(), 7) : subMonths(new Date(), 1);
  return isAfter(t, cutoff);
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

function VerdictBadge({verdict}: {verdict: VerdictKind}): React.JSX.Element {
  // Vocabulary matches the Inbox card buttons: Yes / No / Skip.
  // The internal `approved` / `blocked` / `skipped` keys stay (they
  // map to the underlying VideoRating + agent-agreement model);
  // only the parent-facing label changes.
  const config =
    verdict === 'approved'
      ? {bg: C.emerald100, color: C.emerald700, label: 'Yes', Icon: CheckRounded}
      : verdict === 'blocked'
        ? {bg: C.rose100, color: C.rose700, label: 'No', Icon: CloseRounded}
        : {bg: C.slate100, color: C.slate600, label: 'Skip', Icon: RotateRightRounded};
  const {Icon} = config;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        borderRadius: 999,
        px: 1.25,
        py: 0.5,
        fontSize: 12,
        fontWeight: 700,
        bgcolor: config.bg,
        color: config.color,
      }}
    >
      <Icon sx={{fontSize: 12}} />
      {config.label}
    </Box>
  );
}

interface AINoteProps {
  agreement: AgreementType | null;
  aiScore: number | null;
  verdict: VerdictKind;
}

function AINote({agreement, aiScore, verdict}: AINoteProps): React.JSX.Element {
  // Four states, matching the reference exactly. Phrasing is
  // load-bearing here — "you agreed" gives the parent agency, not
  // the AI.
  if (agreement === AgreementType.Agree && aiScore != null) {
    return (
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{mt: 0.5, color: C.slate500, fontSize: 12}}
      >
        <AutoAwesomeRounded sx={{fontSize: 12, color: C.indigo400}} />
        <span>AI scored this {aiScore.toFixed(1)} — you agreed.</span>
      </Stack>
    );
  }
  if (agreement === AgreementType.Disagree && aiScore != null) {
    return (
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{mt: 0.5, color: C.amber700, fontSize: 12}}
      >
        <WarningAmberRounded sx={{fontSize: 12, color: C.amber500}} />
        <span>AI scored this {aiScore.toFixed(1)} — you went the other way.</span>
      </Stack>
    );
  }
  if (verdict === 'skipped' && aiScore != null) {
    return (
      <Typography sx={{mt: 0.5, fontSize: 12, color: C.slate500}}>
        AI scored this {aiScore.toFixed(1)}. You skipped.
      </Typography>
    );
  }
  return (
    <Typography sx={{mt: 0.5, fontSize: 12, color: C.slate500}}>
      GoodShowHQ hadn't reviewed this yet — your call.
    </Typography>
  );
}

interface ActionsMenuProps {
  verdict: VerdictKind;
  onUndo: () => void;
  onChangeApproved: () => void;
  onChangeBlocked: () => void;
  onOpenYouTube: () => void;
}

function ActionsMenu({
  verdict,
  onUndo,
  onChangeApproved,
  onChangeBlocked,
  onOpenYouTube,
}: ActionsMenuProps): React.JSX.Element {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);
  const close = () => setAnchor(null);
  const wrap = (fn: () => void) => () => {
    close();
    fn();
  };
  return (
    <>
      <IconButton
        size="small"
        onClick={e => setAnchor(e.currentTarget)}
        sx={{
          width: 32,
          height: 32,
          color: C.slate400,
          '&:hover': {bgcolor: C.slate100, color: C.slate700},
        }}
        aria-label="More actions"
      >
        <MoreHorizRounded sx={{fontSize: 16}} />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={close}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        transformOrigin={{vertical: 'top', horizontal: 'right'}}
        slotProps={{paper: {sx: {minWidth: 208, borderRadius: 2.5, boxShadow: '0 8px 24px rgba(15,23,42,0.10)'}}}}
      >
        <MenuItem onClick={wrap(onUndo)} sx={{fontSize: 14, color: C.slate700, gap: 1}}>
          <RestartAltRounded sx={{fontSize: 16, color: C.slate400}} />
          Undo this answer
        </MenuItem>
        {verdict !== 'approved' && (
          <MenuItem onClick={wrap(onChangeApproved)} sx={{fontSize: 14, color: C.slate700, gap: 1}}>
            <CheckRounded sx={{fontSize: 16, color: C.emerald500}} />
            Change to Yes
          </MenuItem>
        )}
        {verdict !== 'blocked' && (
          <MenuItem onClick={wrap(onChangeBlocked)} sx={{fontSize: 14, color: C.slate700, gap: 1}}>
            <CloseRounded sx={{fontSize: 16, color: C.rose500}} />
            Change to No
          </MenuItem>
        )}
        <Divider sx={{my: 0.5}} />
        <MenuItem onClick={wrap(onOpenYouTube)} sx={{fontSize: 14, color: C.slate700, gap: 1}}>
          <PlayCircleRounded sx={{fontSize: 16, color: C.slate400}} />
          Open on YouTube
        </MenuItem>
      </Menu>
    </>
  );
}

/**
 * Pill button used in the inline Yes/No row on each VerdictCard.
 * `active` styles the pill as the recorded answer (filled); the
 * inactive variant invites the parent to switch with one tap.
 */
function VerdictButton({
  active,
  kind,
  onClick,
}: {
  active: boolean;
  kind: 'yes' | 'no';
  onClick: () => void;
}): React.JSX.Element {
  const isYes = kind === 'yes';
  const Icon = isYes ? ThumbUpRounded : ThumbDownRounded;
  const activeBg = isYes ? '#10B981' : '#E11D48';
  const activeBgHover = isYes ? '#059669' : '#BE123C';
  const inactiveBorder = isYes ? '#A7F3D0' : '#FECACA';
  const inactiveColor = isYes ? '#047857' : '#B91C1C';
  const inactiveHoverBg = isYes ? '#ECFDF5' : '#FEF2F2';
  return (
    <Button
      variant={active ? 'contained' : 'outlined'}
      size="small"
      onClick={onClick}
      startIcon={<Icon sx={{fontSize: 14}} />}
      sx={{
        borderRadius: 999,
        bgcolor: active ? activeBg : 'transparent',
        color: active ? '#FFFFFF' : inactiveColor,
        borderColor: active ? activeBg : inactiveBorder,
        fontSize: 13,
        fontWeight: 700,
        textTransform: 'none',
        px: 2,
        boxShadow: 'none',
        cursor: active ? 'default' : 'pointer',
        '&:hover': active
          ? {bgcolor: activeBg, boxShadow: 'none'}
          : {bgcolor: inactiveHoverBg, borderColor: inactiveBorder},
      }}
      disableRipple={active}
    >
      {isYes ? 'Yes' : 'No'}
    </Button>
  );
}

interface VerdictCardProps {
  row: VerdictRow;
  expanded: boolean;
  onToggleExpanded: () => void;
  onUndo: () => void;
  onChange: (next: VerdictKind) => void;
  onOpenYouTube: () => void;
}

function VerdictCard({
  row,
  expanded,
  onToggleExpanded,
  onUndo,
  onChange,
  onOpenYouTube,
}: VerdictCardProps): React.JSX.Element {
  // Show the "See details" toggle only when there's something to
  // expand into — an explanation paragraph or any factor key/value
  // pairs. Today we have explanation only; the factor grid renders
  // empty, so the toggle hides on rows with no AI explanation.
  const hasExpandable = row.explanation.length > 0;

  return (
    <Box
      sx={{
        overflow: 'hidden',
        borderRadius: 4,
        border: `1px solid ${C.slate200}`,
        bgcolor: '#FFFFFF',
      }}
    >
      <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 2, p: 2}}>
        {/* Thumbnail. The cached-videos query doesn't expose
            duration today, so the duration badge is omitted in
            this commit; the position is preserved for when it
            ships. */}
        <Box
          sx={{
            position: 'relative',
            flexShrink: 0,
            width: 96,
            height: 64,
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: C.slate200,
            backgroundImage: row.thumbUrl ? `url(${row.thumbUrl})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {!row.thumbUrl && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'rgba(255,255,255,0.85)',
              }}
            >
              <PlayCircleRounded sx={{fontSize: 22}} />
            </Box>
          )}
        </Box>

        {/* Content */}
        <Box sx={{minWidth: 0, flex: 1}}>
          <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1.5}}>
            <Box sx={{minWidth: 0, flex: 1}}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: C.slate900,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {row.title ?? 'Loading…'}
              </Typography>
              <Typography sx={{fontSize: 12, color: C.slate500}}>
                {row.channelName ?? '—'}
              </Typography>
              <AINote agreement={row.agreement} aiScore={row.aiScore} verdict={row.verdict} />
              {/* Inline verdict-change buttons. Same vocabulary as
                  the Inbox cards (Yes / No / Skip) so the parent
                  uses one mental model across both surfaces. The
                  active answer is contained; the alternatives are
                  outlined and one tap to switch. */}
              <Stack direction="row" spacing={1} sx={{mt: 1.5}} flexWrap="wrap">
                <VerdictButton
                  active={row.verdict === 'approved'}
                  kind="yes"
                  onClick={() => row.verdict !== 'approved' && onChange('approved')}
                />
                <VerdictButton
                  active={row.verdict === 'blocked'}
                  kind="no"
                  onClick={() => row.verdict !== 'blocked' && onChange('blocked')}
                />
                <Button
                  size="small"
                  variant="text"
                  onClick={onUndo}
                  sx={{
                    borderRadius: 999,
                    color: C.slate500,
                    fontSize: 13,
                    fontWeight: 600,
                    textTransform: 'none',
                    px: 1.5,
                    '&:hover': {bgcolor: C.slate100},
                  }}
                >
                  Undo
                </Button>
              </Stack>
            </Box>
            <Box sx={{flexShrink: 0, display: 'flex', alignItems: 'flex-start', gap: 1}}>
              <Stack alignItems="flex-end" spacing={0.5}>
                <VerdictBadge verdict={row.verdict} />
                <Typography sx={{fontSize: 10, color: C.slate400}}>
                  {relativeOrShortDate(row.decidedAt)}
                </Typography>
              </Stack>
              <ActionsMenu
                verdict={row.verdict}
                onUndo={onUndo}
                onChangeApproved={() => onChange('approved')}
                onChangeBlocked={() => onChange('blocked')}
                onOpenYouTube={onOpenYouTube}
              />
            </Box>
          </Box>

          {hasExpandable && (
            <Box
              component="button"
              onClick={onToggleExpanded}
              sx={{
                all: 'unset',
                mt: 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: 12,
                fontWeight: 600,
                color: C.indigo600,
                cursor: 'pointer',
                '&:hover': {color: C.indigo800},
                '&:focus-visible': {outline: `2px solid ${C.indigo500}`, outlineOffset: 2, borderRadius: 4},
              }}
            >
              {expanded ? 'Hide details' : 'See details'}
              {expanded ? (
                <ExpandLessRounded sx={{fontSize: 14}} />
              ) : (
                <ExpandMoreRounded sx={{fontSize: 14}} />
              )}
            </Box>
          )}
        </Box>
      </Box>

      {expanded && hasExpandable && (
        <Box sx={{borderTop: `1px solid ${C.slate100}`, bgcolor: C.slate50, p: 2}}>
          <Typography sx={{fontSize: 14, lineHeight: 1.6, color: C.slate700}}>
            {row.explanation}
          </Typography>
          {/* Factor grid is intentionally empty in this commit —
              the FeedbackLog payload doesn't carry the AI's
              factor breakdown today. Wire-up follows when the
              schema ships per-factor data. */}
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
}

export default function FeedbackView({client}: Props): React.JSX.Element {
  const {pid} = useParams<{pid: string}>();

  // Single source of truth: the same `recentActivity` resolver
  // mobile uses. Server-side dedupes feedback per-video and
  // restricts ai-tone rows to TAKEN + DISMISSED. We filter
  // client-side to vote-tone rows for this page.
  const {data: activityData, loading, error, refetch} = useRecentActivity(
    client,
    pid as string,
    100,
  );
  const {data: profilesData} = useGetMyYouTubeProfiles(client);

  const {mutateAsync: retractVerdict} = useRetractVerdict(client);
  const {mutateAsync: scoreVideo} = useScoreVideo(client);

  const [filter, setFilter] = useState<FilterKind>('all');
  const [scope, setScope] = useState<TimeScope>('month');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [pendingActionRowId, setPendingActionRowId] = useState<string | null>(null);
  // Optimistic verdict overrides keyed by feedback row id. When a
  // user clicks "Change to Blocked", we flip the row's verdict
  // here immediately and reconcile from the next refetch.
  const [optimisticVerdicts, setOptimisticVerdicts] = useState<Record<string, VerdictKind>>({});
  const [toast, setToast] = useState<{severity: 'success' | 'warning' | 'error'; message: string} | null>(null);

  const profile = useMemo(() => {
    const all = profilesData?.getMyYouTubeProfiles ?? [];
    return all.find(p => p?.id === pid) ?? null;
  }, [profilesData, pid]);
  const kidName = profile?.name ?? 'this kid';

  // The `recentActivity` resolver already scopes by profileId,
  // dedupes feedback per-video, and denorms title / channel /
  // thumbnail — so the per-row hydration the prior implementation
  // ran is unnecessary. Apply only the page-local time scope and
  // optimistic overrides here.
  const allRows: VerdictRow[] = useMemo(() => {
    const activity = activityData?.recentActivity ?? [];
    return activity
      .map(activityToRow)
      .filter((r): r is VerdictRow => r !== null)
      .filter(r => inScope(r.decidedAt, scope))
      .map(row => {
        const opt = optimisticVerdicts[row.id];
        return opt ? {...row, verdict: opt} : row;
      })
      .sort((a, b) => (a.decidedAt < b.decidedAt ? 1 : -1));
  }, [activityData, scope, optimisticVerdicts]);

  const counts = useMemo(() => {
    const c: Record<FilterKind, number> = {all: 0, approved: 0, blocked: 0, skipped: 0};
    for (const r of allRows) {
      c.all += 1;
      c[r.verdict] += 1;
    }
    return c;
  }, [allRows]);

  const filtered = useMemo(() => {
    return allRows.filter(r => {
      if (filter !== 'all' && r.verdict !== filter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!(r.title ?? '').toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [allRows, filter, search]);

  // ---- Action handlers --------------------------------------------------

  async function handleUndo(row: VerdictRow) {
    if (!pid || pendingActionRowId) return;
    setPendingActionRowId(row.id);
    try {
      const result = await retractVerdict({profileId: pid, videoId: row.videoId});
      if (result.partial) {
        setToast({
          severity: 'warning',
          message:
            "We've reverted the verdict; the channel block has already been applied. Open the dashboard to fully reverse.",
        });
      } else {
        setToast({severity: 'success', message: 'Verdict undone.'});
      }
      // Refetch so the list drops the row server-side; clear any
      // local optimistic flip for this row at the same time.
      setOptimisticVerdicts(prev => {
        const next = {...prev};
        delete next[row.id];
        return next;
      });
      await refetch();
    } catch (err) {
      setToast({
        severity: 'error',
        message: `Undo failed: ${err instanceof Error ? err.message : String(err)}`,
      });
    } finally {
      setPendingActionRowId(null);
    }
  }

  async function handleChange(row: VerdictRow, next: VerdictKind) {
    if (!pid || pendingActionRowId) return;
    if (next === row.verdict) return;
    setPendingActionRowId(row.id);
    // Optimistic: flip the badge immediately so the click reads
    // as immediate. Reverted on either mutation failure.
    setOptimisticVerdicts(prev => ({...prev, [row.id]: next}));
    try {
      // Retract the prior verdict, then re-score with the new
      // direction. Two mutations (rather than one upsert) so the
      // existing Group 2 spec's wiring works without additions.
      await retractVerdict({profileId: pid, videoId: row.videoId});
      const verdictArg = next === 'approved' ? 'BENEFICIAL' : 'HARMFUL';
      await scoreVideo({profileId: pid, videoId: row.videoId, verdict: verdictArg});
      setToast({severity: 'success', message: `Changed to ${next === 'approved' ? 'Approved' : 'Blocked'}.`});
      await refetch();
      setOptimisticVerdicts(prev => {
        const m = {...prev};
        delete m[row.id];
        return m;
      });
    } catch (err) {
      // Revert.
      setOptimisticVerdicts(prev => {
        const m = {...prev};
        delete m[row.id];
        return m;
      });
      setToast({
        severity: 'error',
        message: `Change failed: ${err instanceof Error ? err.message : String(err)}`,
      });
    } finally {
      setPendingActionRowId(null);
    }
  }

  function handleOpenYouTube(row: VerdictRow) {
    window.open(row.videoUrl, '_blank', 'noopener,noreferrer');
  }

  // ---- Render ------------------------------------------------------------

  const filterChips: Array<{id: FilterKind; label: string}> = [
    {id: 'all', label: 'All'},
    {id: 'approved', label: 'Approved'},
    {id: 'blocked', label: 'Blocked'},
    {id: 'skipped', label: 'Skipped'},
  ];

  return (
    <Container maxWidth="md" sx={{py: 5}}>
      {/* No page-level back link — `ProfileLayout` already renders
          a "← back to {profile name}" breadcrumb above any non-home
          profile route. */}

      {/* Page header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: {xs: 'flex-start', sm: 'flex-end'},
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            component="h1"
            sx={{fontSize: 30, fontWeight: 800, letterSpacing: '-0.02em', color: C.slate900}}
          >
            Your feedback
          </Typography>
          <Typography sx={{mt: 0.5, fontSize: 14, color: C.slate500}}>
            Per-video Yes / No swipes you've made for {kidName} ·{' '}
            {counts.all} answer{counts.all === 1 ? '' : 's'} · tap a button on any row to change your mind. What
            GoodShowHQ actually did on YouTube lives in{' '}
            <Box
              component={RouterLink}
              to={`/profile/${pid}/activity/youtube`}
              sx={{color: C.indigo600, fontWeight: 600, textDecoration: 'none', '&:hover': {textDecoration: 'underline'}}}
            >
              Actions taken
            </Box>
            .
          </Typography>
        </Box>

        {/* Time-scope segmented control — hidden on mobile, default
            stays "This month". */}
        <Box
          sx={{
            display: {xs: 'none', sm: 'flex'},
            alignItems: 'center',
            gap: 0.5,
            borderRadius: 999,
            border: `1px solid ${C.slate200}`,
            bgcolor: '#FFFFFF',
            p: 0.5,
          }}
        >
          {(
            [
              ['week', 'This week'],
              ['month', 'This month'],
              ['all', 'All time'],
            ] as Array<[TimeScope, string]>
          ).map(([id, label]) => {
            const active = scope === id;
            return (
              <Box
                key={id}
                component="button"
                onClick={() => setScope(id)}
                sx={{
                  all: 'unset',
                  cursor: 'pointer',
                  borderRadius: 999,
                  px: 1.75,
                  py: 0.75,
                  fontSize: 12,
                  fontWeight: active ? 700 : 600,
                  color: active ? '#FFFFFF' : C.slate500,
                  bgcolor: active ? C.slate900 : 'transparent',
                  '&:hover': active ? undefined : {color: C.slate900},
                }}
              >
                {label}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Filter chips + search */}
      <Box sx={{mt: 3, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
        {filterChips.map(f => {
          const active = filter === f.id;
          return (
            <Box
              key={f.id}
              component="button"
              onClick={() => setFilter(f.id)}
              sx={{
                all: 'unset',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                borderRadius: 999,
                border: `1px solid ${active ? C.indigo300 : C.slate200}`,
                bgcolor: active ? C.indigo50 : '#FFFFFF',
                color: active ? C.indigo700 : C.slate600,
                px: 1.5,
                py: 0.75,
                fontSize: 12,
                fontWeight: active ? 700 : 600,
                '&:hover': active
                  ? undefined
                  : {borderColor: C.slate300, bgcolor: C.slate50},
              }}
            >
              {f.label}
              <Box
                component="span"
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: active ? C.indigo400 : C.slate400,
                }}
              >
                {counts[f.id]}
              </Box>
            </Box>
          );
        })}

        <Box
          sx={{
            ml: 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            borderRadius: 999,
            border: `1px solid ${C.slate200}`,
            bgcolor: '#FFFFFF',
            px: 1.5,
            py: 0.75,
          }}
        >
          <SearchRounded sx={{fontSize: 14, color: C.slate400}} />
          <InputBase
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by title…"
            sx={{
              fontSize: 12,
              color: C.slate700,
              width: {xs: 160, sm: 224},
              '& input': {p: 0, '&::placeholder': {color: C.slate400, opacity: 1}},
            }}
          />
        </Box>
      </Box>

      {/* List */}
      <Stack spacing={1.5} sx={{mt: 3}}>
        {loading && allRows.length === 0 && (
          <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
            <CircularProgress size={24} sx={{color: C.indigo600}} />
          </Box>
        )}
        {error && (
          <Alert severity="error" variant="outlined">
            Couldn't load your feedback: {error.message}
          </Alert>
        )}
        {!loading && !error && filtered.map(row => (
          <VerdictCard
            key={row.id}
            row={row}
            expanded={expandedId === row.id}
            onToggleExpanded={() =>
              setExpandedId(prev => (prev === row.id ? null : row.id))
            }
            onUndo={() => handleUndo(row)}
            onChange={next => handleChange(row, next)}
            onOpenYouTube={() => handleOpenYouTube(row)}
          />
        ))}
        {!loading && !error && filtered.length === 0 && (
          <Box
            sx={{
              borderRadius: 4,
              border: `2px dashed ${C.slate200}`,
              bgcolor: '#FFFFFF',
              p: 5,
              textAlign: 'center',
            }}
          >
            <Typography sx={{fontSize: 14, fontWeight: 700, color: C.slate900}}>
              No verdicts match these filters.
            </Typography>
            <Typography sx={{mt: 0.5, fontSize: 12, color: C.slate500}}>
              Try a different filter or clear the search.
            </Typography>
          </Box>
        )}
      </Stack>

      {/* Mobile cross-promo. Shown unconditionally on the web for
          now — the desktop tray's `hasRegisteredMobileDevice` flag
          isn't exposed via GraphQL yet. When that lands, gate this
          on the same check. */}
      <Typography sx={{mt: 4, textAlign: 'center', fontSize: 12, color: C.slate400}}>
        Want to undo something quickly?{' '}
        <Box
          component="a"
          href="https://goodshowhq.app/get"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            fontWeight: 600,
            color: C.indigo600,
            textDecoration: 'none',
            '&:hover': {textDecoration: 'underline'},
          }}
        >
          Open the review queue on mobile ↗
        </Box>
      </Typography>

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            variant="filled"
            onClose={() => setToast(null)}
            sx={{width: '100%'}}
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Container>
  );
}
